import { useCallback } from 'react';

import * as Sentry from '@sentry/react';

import { DataTable, LogPayload, RowKey } from '@cast/design-system';

import { SearchableTableProps } from './types';
import { useSearchContext } from '../hooks';
import { getNoDataState } from '../utils';

export const SearchableTable = <T = any, K extends RowKey = RowKey>({
  children,
  components,
  hasFailed,
  onDataSort,
  onLoadMore,
  ...rest
}: SearchableTableProps<T, K>) => {
  const ctx = useSearchContext();
  if (!ctx) {
    throw new Error('SearchableTable has to be used in a SearchContext');
  }

  const { component: noDataComponent } = getNoDataState(ctx, {
    components,
    hasFailed: !!ctx.error || hasFailed,
  });

  const log = useCallback(({ message, data, logLevel }: LogPayload) => {
    Sentry.captureMessage(message, { level: logLevel, extra: data });
  }, []);

  const handleLoadMoreDefault =
    !ctx.isLoading && !ctx.isFetching && ctx.hasNextPage
      ? ctx.fetchNextPage
      : undefined;

  const handleLoadMore = onLoadMore ?? handleLoadMoreDefault;

  return (
    <>
      {!noDataComponent && (
        <DataTable
          {...rest}
          onLoadMore={handleLoadMore}
          onDataSort={(newState) => {
            onDataSort?.(newState);
            if (ctx?.serverSideSort) {
              ctx?.setSortingState?.(newState);
            }
          }}
          onSortingChanged={!ctx.serverSideSort ? ctx.setSortFn : undefined}
          controlledSorting={ctx.serverSideSort}
          data={ctx.filteredData}
          isLoading={ctx.isLoading}
          isFetching={ctx.isFetching}
          log={log}
          totalDataLength={ctx.data?.length}
          urlKey={ctx.urlKey}
        >
          {children}
        </DataTable>
      )}
      {noDataComponent}
    </>
  );
};
